import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  Gauge,
  FileText,
  CheckCircle,
  Car,
  User,
  Shield,
  MessageSquare,
  Users,
  Mail,
  Phone,
  CreditCard,
  Clock,
  Euro,
  Wrench,
  Briefcase,
  Activity,
  Paperclip,
  Eye,
  XCircle,
  ChevronDown,
  Loader2,
  ZoomIn,
  ZoomOut,
  Download,
  RefreshCw,
} from 'lucide-react';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DecisionStatus } from '@/models/savV2Decision';

import { DetailHeader } from '@/Components/shared/DetailHeader';
import { InfoCard, InfoRow } from '@/Components/shared/InfoCard';
import { ProgressTimeline } from '@/Components/shared/ProgressTimeline';
import { savStatusToVariant, savStatusLabel } from '@/Components/shared/StatusBadge';
import { EmptyState } from '@/Components/shared/EmptyState';
import { formatDate, formatDateTime, formatElapsedDuration } from '@/utils/dateUtils';
import { formatMileage } from '@/utils/formatters';
import { formatPrice } from '@/features/contracts/utils/contractFormatter';
import { DURATION_LABELS } from '@/features/warranties/types/warranty.types';
import {
  getVehicleBrand,
  getVehicleModel,
  getVehicleRegistration,
  getVehicleFirstRegistrationDate,
  getVehicleMileage,
  getVehiclePurchaseDate,
} from '@/utils/contractHelpers';
import { useSavDetail } from '../hooks/useSavDetail';

import { SavTechnicalBlock } from '../components/SavTechnicalBlock';

import { usePermissions } from '@/hooks/auth/usePermissions';
import { useAuthStore } from '@/Stores/auth-v2-store';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { notifications } from '@/routes/outil-pro/utils/notifications';
import { savApi } from '../api/savApi';
import { ISavV2 } from '@/models/savV2';
import { SavGarantMessageModal } from '../components/SavGarantMessageModal';
import { IDocument } from '@/models/document';
import DocumentViewer from '@/Components/DocumentViewer/DocumentViewer';
import SavV2MessagesApi, { type Message } from '@/Api/sav-v2/savV2MessagesApi';
import { savV2DocumentApi } from '@/Api/sav-v2/savV2DocumentApi';
import { SavV2DocumentsData, DocumentReference, LabeledDocument } from '@/models/savV2Documents';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from '@/Api/axiosV2Config';
import { SavV2EventType, type SavV2Event } from '@/models/savV2Event';
import { getEventDetailRows, getTimelineDescription, getTimelineEventConfig } from '../utils/savTimelineEvents';
import { SavDecisionTab } from '../components/SavDecisionTab';
import { GarantDirectCoverageView } from '../components/GarantDirectCoverageView';
import RepairerPaymentSection from '../components/RepairerPaymentSection';
import { SavChatBlock } from '../components/SavChatBlock';
import { SavDocumentGrid } from '../components/SavDocumentGrid';
import { useContractFinancialCap } from '@/routes/gestion-sav/SavV2/hooks/useSavV2Queries';
import { savV2Api } from '@/Api/sav-v2/savV2Api';
import { DeclarationStep } from '../components/steps/DeclarationStep';
import { Document, Page } from '@/Components/shared/react-pdf-lazy';
import { getFileNameFromHeaders, getResponseMimeType, resolveDownloadFileName, triggerBlobDownload } from '@/utils/fileDownload';

function V1SavDetail({ sav }: { sav: ISavV2 }) {
  const [v1NumPages, setV1NumPages] = useState(0);
  const [v1Scale, setV1Scale] = useState(1.0);
  const [v1ImageError, setV1ImageError] = useState(false);

  // Le bon v1 est un PDF (Yousign) ou un scan/photo uploadé à la main par le
  // vendeur : on garde son type réel pour l'afficher et le nommer.
  const {
    data: v1Doc,
    isLoading: v1DocLoading,
    isError: v1DocError,
  } = useQuery({
    queryKey: ['sav-v1-decision-voucher', sav.id, sav.decisionVoucherDocId],
    enabled: !!sav.decisionVoucherDocId,
    staleTime: Infinity,
    queryFn: async () => {
      const token = await savApi.getDocumentDownloadToken(sav.id, sav.decisionVoucherDocId!);
      const response = await axiosInstance.get<Blob>(
        `/documents/${sav.decisionVoucherDocId}/download?downloadType=download&documentDownloadToken=${token}`,
        { responseType: 'blob' },
      );
      // Type absent ou générique (ancienne API) : historiquement un PDF.
      const received = getResponseMimeType(response);
      const type = !received || received === 'application/octet-stream' || received.startsWith('text/')
        ? 'application/pdf'
        : received;
      return {
        blob: new Blob([response.data], { type }),
        fileName: resolveDownloadFileName(getFileNameFromHeaders(response.headers), type, `bon-decision-${sav.id}`),
      };
    },
  });

  const v1DocUrl = useMemo(() => (v1Doc ? URL.createObjectURL(v1Doc.blob) : null), [v1Doc]);

  // Libère l'object URL (ressource navigateur) quand il change ou au démontage.
  useEffect(() => {
    return () => { if (v1DocUrl) URL.revokeObjectURL(v1DocUrl); };
  }, [v1DocUrl]);

  const v1DocKind: 'pdf' | 'image' | 'other' | null = !v1Doc
    ? null
    : v1Doc.blob.type === 'application/pdf'
      ? 'pdf'
      : /^image\/(jpeg|png|gif|webp|bmp|avif)$/.test(v1Doc.blob.type) ? 'image' : 'other';

  const handleV1Download = () => {
    if (v1Doc) triggerBlobDownload(v1Doc.blob, v1Doc.fileName);
  };

  return (
    <div className="tw-space-y-4">
      {/* Déclaration */}
      <DeclarationStep sav={sav} />

      {/* Bannière action requise */}
      {sav.awaitingGarant && (
        <div className="tw-bg-orange-50 tw-border tw-border-orange-200 tw-rounded-lg tw-p-4 tw-text-center">
          <AlertCircle className="tw-w-6 tw-h-6 tw-text-orange-500 tw-mx-auto tw-mb-2" />
          <p className="tw-text-sm tw-font-medium tw-text-orange-800 tw-mb-0">En attente d'action du garant</p>
        </div>
      )}

      {/* Montants */}
      {(sav.amountSeller || sav.amountSellerCommercial || sav.amountBeneficiary) && (
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
          <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-gray-900 tw-uppercase tw-px-4 tw-py-3 tw-border-b tw-border-gray-200 tw-m-0">
            <Euro className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
            Synthèse financière
          </h3>
          <div className="tw-p-4">
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3 sm:tw-gap-4">
              <div className="tw-bg-green-50 tw-rounded-lg tw-p-3 tw-text-center">
                <p className="tw-text-xs tw-text-gray-500 tw-mb-1">Montant vendeur</p>
                <p className="tw-text-lg tw-font-semibold tw-text-green-700 tw-mb-0">
                  {sav.amountSeller ? `${parseFloat(String(sav.amountSeller)).toFixed(2)} €` : '0,00 €'}
                </p>
              </div>
              <div className="tw-bg-blue-50 tw-rounded-lg tw-p-3 tw-text-center">
                <p className="tw-text-xs tw-text-gray-500 tw-mb-1">Geste commercial</p>
                <p className="tw-text-lg tw-font-semibold tw-text-blue-700 tw-mb-0">
                  {sav.amountSellerCommercial ? `${parseFloat(String(sav.amountSellerCommercial)).toFixed(2)} €` : '0,00 €'}
                </p>
              </div>
              <div className="tw-bg-red-50 tw-rounded-lg tw-p-3 tw-text-center">
                <p className="tw-text-xs tw-text-gray-500 tw-mb-1">Montant bénéficiaire</p>
                <p className="tw-text-lg tw-font-semibold tw-text-red-700 tw-mb-0">
                  {sav.amountBeneficiary ? `${parseFloat(String(sav.amountBeneficiary)).toFixed(2)} €` : '0,00 €'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bon de décision */}
      {sav.decisionVoucher && (
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
          <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-gray-900 tw-uppercase tw-px-4 tw-py-3 tw-border-b tw-border-gray-200 tw-m-0">
            <CheckCircle className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
            Bon de décision
          </h3>
          <div className="tw-p-4 tw-space-y-4">
            {sav.decisionVoucher.decision && (
              <div className="tw-flex tw-items-center tw-gap-2">
                <span className="tw-text-sm tw-text-gray-600">Type de décision :</span>
                <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium ${
                  sav.decisionVoucher.decision === 'partialOrTotalAgreement'
                    ? 'tw-bg-green-100 tw-text-green-800'
                    : sav.decisionVoucher.decision === 'commercialGesture'
                      ? 'tw-bg-blue-100 tw-text-blue-800'
                      : 'tw-bg-red-100 tw-text-red-800'
                }`}>
                  {sav.decisionVoucher.decision === 'partialOrTotalAgreement' ? 'Accord' :
                   sav.decisionVoucher.decision === 'commercialGesture' ? 'Geste commercial' :
                   sav.decisionVoucher.decision === 'refusal' ? 'Refus' : sav.decisionVoucher.decision}
                </span>
              </div>
            )}
            {sav.decisionVoucher.comment && (
              <div>
                <span className="tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">Commentaire</span>
                <p className="tw-text-sm tw-text-gray-700 tw-mb-0 tw-mt-0.5 tw-whitespace-pre-line">{sav.decisionVoucher.comment}</p>
              </div>
            )}

            {/* PDF inline */}
            {sav.decisionVoucherDocId && (
              <div>
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <FileText className="tw-w-4 tw-h-4 tw-text-gray-400" />
                    <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Document</span>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <button onClick={() => setV1Scale(s => Math.max(0.5, s - 0.25))} disabled={v1Scale <= 0.5} className="tw-p-1.5 tw-text-gray-600 hover:tw-bg-gray-200 tw-rounded tw-transition-colors disabled:tw-opacity-50 tw-bg-transparent tw-border-none tw-cursor-pointer">
                      <ZoomOut className="tw-w-4 tw-h-4" />
                    </button>
                    <span className="tw-text-xs tw-text-gray-600 tw-min-w-[45px] tw-text-center">{Math.round(v1Scale * 100)}%</span>
                    <button onClick={() => setV1Scale(s => Math.min(2.0, s + 0.25))} disabled={v1Scale >= 2.0} className="tw-p-1.5 tw-text-gray-600 hover:tw-bg-gray-200 tw-rounded tw-transition-colors disabled:tw-opacity-50 tw-bg-transparent tw-border-none tw-cursor-pointer">
                      <ZoomIn className="tw-w-4 tw-h-4" />
                    </button>
                    {v1DocUrl && (
                      <button onClick={handleV1Download} className="tw-p-1.5 tw-text-gray-600 hover:tw-bg-gray-200 tw-rounded tw-transition-colors tw-bg-transparent tw-border-none tw-cursor-pointer">
                        <Download className="tw-w-4 tw-h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="tw-max-h-[500px] tw-overflow-y-auto tw-bg-gray-100 tw-p-4">
                  {v1DocLoading && (
                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16">
                      <Loader2 className="tw-w-8 tw-h-8 tw-animate-spin tw-text-affinicar-primary tw-mb-3" />
                      <p className="tw-text-sm tw-text-gray-500">Chargement du bon de décision...</p>
                    </div>
                  )}
                  {v1DocError && (
                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16">
                      <p className="tw-text-sm tw-text-red-600 tw-mb-1">Impossible de charger le bon de décision.</p>
                    </div>
                  )}
                  {v1DocUrl && (v1DocKind === 'other' || (v1DocKind === 'image' && v1ImageError)) && (
                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16">
                      <p className="tw-text-sm tw-text-gray-600 tw-mb-2">Aperçu indisponible pour ce format.</p>
                      <button onClick={handleV1Download} className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-text-affinicar-primary tw-bg-transparent tw-border-none tw-cursor-pointer hover:tw-underline">
                        <Download className="tw-w-4 tw-h-4" /> Télécharger le document
                      </button>
                    </div>
                  )}
                  {v1DocUrl && v1DocKind === 'image' && !v1ImageError && (
                    // mx-auto (et non justify-center) : zoomée au-delà de 100 %, l'image
                    // reste atteignable par le scroll des deux côtés.
                    <img
                      src={v1DocUrl}
                      alt="Bon de décision"
                      onError={() => setV1ImageError(true)}
                      style={{ width: `${v1Scale * 100}%` }}
                      className="tw-block tw-mx-auto tw-max-w-none tw-shadow-lg"
                    />
                  )}
                  {v1DocUrl && v1DocKind === 'pdf' && (
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
                      <Document
                        file={v1DocUrl}
                        onLoadSuccess={({ numPages }) => setV1NumPages(numPages)}
                        loading={<div className="tw-text-center tw-py-8"><Loader2 className="tw-w-6 tw-h-6 tw-animate-spin tw-text-affinicar-primary tw-mx-auto" /></div>}
                        error={<div className="tw-text-center tw-py-8 tw-text-red-600"><p className="tw-text-sm">Erreur lors du rendu du PDF</p></div>}
                      >
                        {Array.from(new Array(v1NumPages), (_, index) => (
                          <Page key={`page_${index + 1}`} pageNumber={index + 1} scale={v1Scale} renderTextLayer={false} renderAnnotationLayer={false} className="tw-shadow-lg tw-mb-2" />
                        ))}
                      </Document>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface SavDetailPageProps {
  savId?: number;
  onBack?: () => void;
  isAdmin?: boolean;
  embedded?: boolean;
}

const declarationDocLabels: Record<string, string> = {
  initialQuote: 'Devis initial',
  initialInvoice: 'Facture initiale',
  initialDiagnostic: 'Diagnostic initial',
  registrationCard: 'Carte grise',
  mileagePhoto: 'Photo kilométrage',
  technicalControl: 'Contrôle technique',
  maintenanceBook: 'Carnet d\'entretien',
  vehiclePhotos: 'Photo véhicule',
  maintenanceInvoices: 'Facture entretien',
  others: 'Autre document',
};

// === Helpers pour statuts virtuels, devis, decision, intervention ===

function phaseToVariant(phase?: string): 'in_progress' | 'pending_signature' | 'sent' | 'active' | 'completed' | 'no_action' | 'default' {
  switch (phase) {
    case 'declaration': return 'active';
    case 'quote_acquisition': return 'pending_signature';
    case 'analysis': return 'sent';
    case 'validation': return 'active';
    case 'repair': return 'in_progress';
    case 'completed': return 'completed';
    default: return 'default';
  }
}

function actionRequiredLabel(action?: string): { label: string; color: string } | null {
  switch (action) {
    case 'beneficiary': return { label: 'Action requise : Bénéficiaire', color: 'tw-bg-orange-50 tw-text-orange-700 tw-border-orange-200' };
    case 'manager': return { label: 'Action requise : Gestionnaire', color: 'tw-bg-blue-50 tw-text-blue-700 tw-border-blue-200' };
    case 'garant': return { label: 'Action requise : Garant', color: 'tw-bg-red-50 tw-text-red-700 tw-border-red-200' };
    case 'repairer': return { label: 'Action requise : Réparateur', color: 'tw-bg-green-50 tw-text-green-700 tw-border-green-200' };
    default: return null;
  }
}

function v1StatusLabel(stateId?: number): string {
  switch (stateId) {
    case 2: return 'Nouveau';
    case 3: return 'En attente bénéficiaire';
    case 4: return 'En attente gestionnaire';
    case 5: return 'Litige';
    case 6: return 'En attente garant';
    case 7: return 'À prendre en charge';
    case 8: return 'Pris en charge';
    case 9: return 'Sans suite';
    default: return 'Inconnu';
  }
}

function v1StatusVariant(stateId?: number): 'in_progress' | 'completed' | 'no_action' | 'pending_signature' | 'active' | 'default' {
  switch (stateId) {
    case 2: return 'active';
    case 3: case 4: case 6: return 'pending_signature';
    case 5: return 'no_action';
    case 7: return 'in_progress';
    case 8: return 'completed';
    case 9: return 'no_action';
    default: return 'default';
  }
}

function toIDocument(ref: DocumentReference | LabeledDocument, label: string): IDocument {
  return {
    id: ref.documentId,
    label: ('label' in ref && ref.label) || label,
    originalName: ref.originalFileName,
    dateCreation: ref.uploadedAt,
  } as IDocument;
}

function getDeclarationDocuments(data?: SavV2DocumentsData | null): IDocument[] {
  if (!data) return [];
  const docs: IDocument[] = [];
  const singleKeys: (keyof SavV2DocumentsData)[] = [
    'initialQuote', 'initialInvoice', 'initialDiagnostic',
    'registrationCard', 'mileagePhoto', 'technicalControl', 'maintenanceBook',
  ];
  for (const key of singleKeys) {
    const ref = data[key] as DocumentReference | undefined;
    if (ref?.documentId) docs.push(toIDocument(ref, declarationDocLabels[key] || key));
  }
  const arrayKeys: (keyof SavV2DocumentsData)[] = ['vehiclePhotos', 'maintenanceInvoices', 'others'];
  for (const key of arrayKeys) {
    const arr = data[key] as LabeledDocument[] | undefined;
    if (arr) {
      for (const ref of arr) {
        if (ref.documentId) docs.push(toIDocument(ref, declarationDocLabels[key] || key));
      }
    }
  }
  return docs;
}

export function SavDetailPage({ savId: savIdProp, onBack, isAdmin, embedded }: SavDetailPageProps = {}) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const savId = savIdProp || parseInt(id || '0');
  const handleBack = onBack || (() => navigate(-1));

  const { can } = usePermissions();
  const { currentUser } = useAuthStore();
  const queryClient = useQueryClient();
  const { data: effectiveConfig } = useEffectiveConfiguration(currentUser?.societyId);
  // `hasGuarantor` = un garant EXTERNE (autre société) prend le relais de la
  // signature. L'auto-garant se représente normalement par guarantor_id = NULL,
  // mais certaines configs le stockent en pointant sur leur propre société :
  // dans ce cas la société EST le garant et doit pouvoir signer. On exclut donc
  // le cas guarantor_id === sa propre société (sinon le bouton disparaît à tort).
  const hasGuarantor =
    !!effectiveConfig?.guarantorId &&
    effectiveConfig.guarantorId !== currentUser?.societyId;
  const canSign = can('sav.sign') && !hasGuarantor;

  const [isOpeningValidation, setIsOpeningValidation] = useState(false);
  const [showGarantMessageModal, setShowGarantMessageModal] = useState(false);
  const { sav, virtualStatuses, isLoading, error } = useSavDetail(savId);
  const profileTargetSocietyId = currentUser?.type === 'INTERNAL'
    ? sav?.contract?.company?.uuid
    : currentUser?.societyId;

  // Badge « messages non lus » garant : on capture le compteur AU PREMIER
  // chargement du SAV (jamais avant), pour l'afficher dans le chat même après
  // le mark-read. Puis, uniquement pour un garant EXTERNE (pas un admin qui
  // consulte) et si >0, on marque lu (reset serveur) + on rafraîchit le badge
  // sidebar. L'affichage « nouveaux messages » persiste via le snapshot local.
  const [garantUnreadSnapshot, setGarantUnreadSnapshot] = useState<number | null>(
    null,
  );
  useEffect(() => {
    if (!sav || garantUnreadSnapshot !== null) return; // capture une seule fois
    const isExternalGarant = currentUser?.type === 'EXTERNAL' && !isAdmin;
    // Admin / gestion qui consulte : ni bandeau (snapshot 0) ni mark-read — le
    // « non lu » appartient au garant, pas à l'admin.
    const n = isExternalGarant ? sav.unreadGarantMessagesCount ?? 0 : 0;
    setGarantUnreadSnapshot(n);
    if (n > 0) {
      savApi
        .markGarantMessagesRead(sav.id)
        .then(() => {
          // Badge sidebar ET détail SAV remis à 0 : sinon, en ressortant/rentrant
          // avant le staleTime, le cache re-sert l'ancien compteur → bandeau fantôme.
          queryClient.invalidateQueries({
            queryKey: ['garant-unread-messages-count'],
          });
          queryClient.invalidateQueries({
            queryKey: ['sav-detail', sav.id],
            exact: true,
          });
          // La liste porte aussi le badge par ligne → clear immédiat au retour.
          queryClient.invalidateQueries({ queryKey: ['sav-list'] });
        })
        .catch(() => {
          /* best-effort : le badge se recalera au prochain tick */
        });
    }
  }, [sav, garantUnreadSnapshot, currentUser?.type, isAdmin, queryClient]);
  const { data: profileConfig } = useEffectiveConfiguration(profileTargetSocietyId);
  const { data: financialCapData } = useContractFinancialCap(sav?.contract?.id);
  const { data: allSavsForContract } = useQuery({
    queryKey: ['hybrid-savs', sav?.contract?.id],
    queryFn: () => savV2Api.getSavsByContractId(sav!.contract!.id, 150),
    enabled: !!sav?.contract?.id,
    staleTime: 1000 * 60,
  });
  const otherSavs = useMemo(
    () => (allSavsForContract || []).filter((s) => s.id !== sav?.id),
    [allSavsForContract, sav?.id],
  );

  // Raccourci depuis le mail garant : avec `?redirect_to_validation=true`, si le
  // garant connecté est éligible (MÊME condition que le bouton « Accéder à la
  // décision »), on le redirige directement vers la page de validation — sans le
  // faire scroller/cliquer. Onglet COURANT (window.location, pas window.open qui
  // serait bloqué hors clic). Consommé UNE seule fois (anti-boucle au « précédent »).
  const autoValidationTriggered = useRef(false);
  useEffect(() => {
    if (autoValidationTriggered.current) return;
    if (searchParams.get('redirect_to_validation') !== 'true') return;
    if (!sav) return; // données pas encore chargées

    autoValidationTriggered.current = true;
    // Retirer le param de l'URL (sinon un « précédent » re-déclencherait).
    const next = new URLSearchParams(searchParams);
    next.delete('redirect_to_validation');
    setSearchParams(next, { replace: true });

    // Éligibilité = exactement celle qui affiche le bouton (dont SAV actif —
    // un sans suite manuel laisse la décision en SENT_TO_GARANT).
    const decision = sav.currentIntervention?.selectedQuote?.currentDecision ?? null;
    const savActive = sav.status === 'ACTIVE';
    const showValidateAndSign = !!(
      savActive &&
      decision &&
      decision.status === DecisionStatus.SENT_TO_GARANT &&
      !decision.signatures?.garant?.signatureLink
    );
    const showSignature = !!(
      savActive &&
      decision &&
      [DecisionStatus.SENT_TO_GARANT, DecisionStatus.PENDING_GARANT_SIGNATURE].includes(decision.status) &&
      decision.signatures?.garant?.signatureLink &&
      !decision.signatures.garant.signedAt
    );
    const eligible = canSign && !isAdmin && (showValidateAndSign || showSignature);
    if (!eligible) return; // pas éligible → on laisse la page s'afficher normalement

    // Même action que le clic : on génère le lien à la volée et on y va.
    (async () => {
      try {
        const { validationUrl } = await savApi.getGarantValidationLink(savId);
        window.location.href = validationUrl; // onglet courant
      } catch {
        notifications.error('Impossible de générer le lien de validation. Veuillez réessayer.');
      }
    })();
  }, [sav, canSign, isAdmin, savId, searchParams, setSearchParams]);

  if (isLoading) {
    return (
      <div className="tw-px-6 tw-py-4 tw-space-y-4 tw-animate-pulse">
        <div className="tw-h-5 tw-w-48 tw-bg-gray-200 tw-rounded" />
        <div className="tw-h-28 tw-bg-gray-200 tw-rounded-xl" />
        <div className="tw-h-12 tw-bg-gray-200 tw-rounded-lg" />
        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="tw-h-36 tw-bg-gray-200 tw-rounded-xl" />
          ))}
        </div>
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
          {[1, 2].map(i => (
            <div key={i} className="tw-h-48 tw-bg-gray-200 tw-rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !sav) {
    return (
      <EmptyState
        icon={<AlertCircle className="tw-w-12 tw-h-12" />}
        title="SAV introuvable"
        description={error || 'Ce dossier SAV n\'existe pas ou une erreur est survenue.'}
        action={{ label: 'Retour à la liste', onClick: handleBack }}
      />
    );
  }

  const isV1Sav = !!(sav.stateId && !sav.currentIntervention && !sav.status);
  const intervention = sav.currentIntervention;
  const quote = intervention?.selectedQuote ?? null;
  const decision = quote?.currentDecision ?? null;

  // === Prise en charge directe par le garant (variante B) ===
  // Détection du workflow direct-coverage côté front : le garant connecté est
  // aussi le réparateur assigné à l'intervention courante, i.e. la société du
  // garant (`currentUser.societyId`) === `intervention.repairerUuid`. C'est
  // exactement la condition qui, côté gestion, déclenche le `DirectCoverageStep`
  // (garant = réparateur). On la combine à `actionRequired === 'garant' && !decision`
  // (variante B : awaitingGarant sans décision) pour la distinguer de la variante A
  // (décision + signature, où l'on garde `SavDecisionTab`).
  // NB : si `repairerUuid` n'était pas exposé sur l'objet sav, on retomberait sur
  // le proxy `actionRequired === 'garant' && !decision`. Ici il l'est, on l'utilise
  // donc comme garde supplémentaire (anti-faux-positif).
  const isDirectCoverageWorkflow = !!(
    intervention?.repairerUuid &&
    currentUser?.societyId &&
    intervention.repairerUuid === currentUser.societyId
  );
  // On détecte le statut DIRECT_COVERAGE N'IMPORTE OÙ dans la liste (pas seulement
  // virtualStatuses[0]) : la DirectCoverageStrategy a une priorité basse (31), donc un
  // message non lu ou un SAV « stale » (actionRequired=manager, priorité plus haute)
  // prendrait la place [0] et masquerait le formulaire par intermittence.
  const isDirectCoverageStatus = virtualStatuses.some((s) => s.status === 'DIRECT_COVERAGE');
  const isGarantDirectCoverage = !!(
    canSign &&
    !isAdmin &&
    !decision &&
    isDirectCoverageWorkflow &&
    isDirectCoverageStatus
  );

  // Le SAV doit être ACTIF : un sans suite MANUEL laisse la décision en
  // SENT_TO_GARANT (dossier abandonné) — sans cette garde, les boutons
  // s'affichent sur un dossier clos et échouent au clic (l'API refuse).
  // Le flux refus n'est pas concerné : le sans suite n'y arrive qu'APRÈS la
  // signature garant, le SAV est ACTIF pendant toute la phase de validation.
  const savIsActive = sav.status === 'ACTIVE';

  const showSignatureButton = !!(
    savIsActive &&
    decision &&
    [DecisionStatus.SENT_TO_GARANT, DecisionStatus.PENDING_GARANT_SIGNATURE].includes(decision.status) &&
    decision.signatures?.garant?.signatureLink &&
    !decision.signatures.garant.signedAt
  );

  const showValidateAndSignButton = !!(
    savIsActive &&
    decision &&
    decision.status === DecisionStatus.SENT_TO_GARANT &&
    !decision.signatures?.garant?.signatureLink
  );

  // Le garant (permission sav.sign) n'accepte/refuse plus en place : on génère
  // un lien de validation à la volée et on l'ouvre dans un nouvel onglet, vers
  // la page de validation dédiée (consultation + signature du bon).
  const handleAccessDecision = async () => {
    if (!decision) return;
    // Ouvrir l'onglet AVANT l'await (sinon bloqué par le popup-blocker).
    const newWindow = window.open('', '_blank');
    setIsOpeningValidation(true);
    try {
      const { validationUrl } = await savApi.getGarantValidationLink(savId);
      if (newWindow) newWindow.location.href = validationUrl;
    } catch {
      if (newWindow) newWindow.close();
      notifications.error('Impossible de générer le lien de validation. Veuillez réessayer.');
    } finally {
      setIsOpeningValidation(false);
    }
  };

  const beneficiary = sav.contract?.beneficiary;
  const warranty = sav.contract?.warranty;
  const contractCap = sav.contract?.warrantyFinancialCap || warranty?.financialCap || 0;
  const totalCap = financialCapData ? financialCapData.totalCap / 100 : contractCap;
  const usedAmount = financialCapData ? financialCapData.usedAmount / 100 : 0;
  const remainingCap = financialCapData ? financialCapData.availableAmount / 100 : contractCap;
  const capUsedPercent = totalCap > 0 ? Math.min(100, Math.round((usedAmount / totalCap) * 100)) : 0;
  const capBarColor =
    capUsedPercent >= 100 ? 'tw-bg-red-500'
    : capUsedPercent >= 80 ? 'tw-bg-amber-500'
    : 'tw-bg-green-500';
  const remainingColor =
    remainingCap <= 0 ? 'tw-text-red-600'
    : remainingCap < totalCap * 0.2 ? 'tw-text-amber-600'
    : 'tw-text-green-600';
  const vehicleBrand = getVehicleBrand(sav.contract);
  const vehicleModel = getVehicleModel(sav.contract);
  const vehicleRegistration = getVehicleRegistration(sav.contract);
  const vehicleFirstRegistrationDate = getVehicleFirstRegistrationDate(sav.contract);
  const declarationDocs = getDeclarationDocuments(sav.documentsData);
  const allSharedDocuments = [...declarationDocs, ...(sav.documentsSharedWithBeneficiary || [])];

  const savTitle = sav.uniqueId || `SAV-${sav.id}`;

  return (
    <div className={embedded ? 'tw-pt-4 tw-pb-8 sm:tw-pb-12' : 'tw-px-3 sm:tw-px-6 tw-pb-8 sm:tw-pb-12'}>
      {/* Header — full width */}
      <div className="tw-mb-4">
        <DetailHeader
          type="sav"
          title={savTitle}
          status={{
            label: isV1Sav
              ? v1StatusLabel(sav.stateId)
              : (virtualStatuses[0]?.label || savStatusLabel(sav.status)),
            variant: isV1Sav
              ? v1StatusVariant(sav.stateId)
              : (virtualStatuses[0] ? phaseToVariant(virtualStatuses[0].phase) : savStatusToVariant(sav.status)),
          }}
          breadcrumb={[
            onBack ? { label: 'SAV', onClick: onBack } : { label: 'SAV', href: '/sav' },
            { label: savTitle },
          ]}
          metadata={[
            ...(sav.contract?.warranty?.name ? [{ label: 'Garantie', value: sav.contract.warranty.name }] : []),
            ...(sav.incidentDate ? [{ label: 'Date de la panne', value: formatDate(sav.incidentDate) }] : []),
            ...(sav.declarationDate ? [{ label: 'Date de déclaration', value: formatDateTime(sav.declarationDate) }] : []),
          ]}
        />
      </div>

      {/* Action required banner */}
      {(() => {
        const actionRequired = virtualStatuses[0]?.actionRequired;
        if (actionRequired === 'garant' && !canSign) return null;
        const actionInfo = actionRequiredLabel(actionRequired);
        if (!actionInfo) return null;
        return (
          <div className={`tw-mb-4 tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-rounded-lg tw-border tw-text-sm tw-font-medium ${actionInfo.color}`}>
            <Activity className="tw-w-4 tw-h-4 tw-flex-shrink-0" />
            {actionInfo.label}
            {virtualStatuses[0]?.description && (
              <span className="tw-font-normal tw-text-xs tw-opacity-75">— {virtualStatuses[0].description}</span>
            )}
            {/* Bouton « Voir la décision » uniquement si une décision existe et
                est affichable : la cible de scroll #step-decision n'est montée
                que si `decision && status !== DRAFT` (cf. SavDecisionTab). En
                prise en charge directe (garant = réparateur, awaitingGarant sans
                décision), actionRequired vaut 'garant' mais il n'y a AUCUNE
                décision → on n'affiche pas ce bouton (sinon scroll dans le vide). */}
            {virtualStatuses[0]?.actionRequired === 'garant' && decision && decision.status !== DecisionStatus.DRAFT && (
              <button
                type="button"
                onClick={() => {
                  const el = document.getElementById('step-decision');
                  if (el) {
                    const offset = 140;
                    const top = el.getBoundingClientRect().top + window.scrollY - offset;
                    window.scrollTo({ top, behavior: 'smooth' });
                  }
                }}
                className="tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-rounded tw-border tw-border-current tw-bg-transparent tw-cursor-pointer hover:tw-opacity-80 tw-transition-opacity"
              >
                Voir la décision
              </button>
            )}
          </div>
        );
      })()}

      {/* Cycle paiement réparateur : encart facture à régler / suivi.
          Rend null si aucune facture visible (SAV hors workflow) —
          zéro impact pour les vendeurs sans cycle paiement. */}
      <RepairerPaymentSection savId={savId} />

      {/* Two-column layout: Decision (3/4) | Sidebar (1/4) */}
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 lg:tw-grid-cols-4 tw-gap-4">

        {/* ===== LEFT: Decision flow (3/4) ===== */}
        <div className="tw-order-2 md:tw-order-1 md:tw-col-span-2 lg:tw-col-span-3">
          {isV1Sav ? (
            <V1SavDetail sav={sav} />
          ) : isGarantDirectCoverage ? (
            <GarantDirectCoverageView
              sav={sav}
              onSuccess={() => {
                queryClient.invalidateQueries({ queryKey: ['sav-detail', savId] });
              }}
            />
          ) : (
            <SavDecisionTab
              sav={sav}
              isV1Sav={isV1Sav}
              quote={quote}
              decision={decision}
              showValidateAndSignButton={canSign && !isAdmin && showValidateAndSignButton}
              showSignatureButton={canSign && !isAdmin && showSignatureButton}
              showContactButton={!isAdmin}
              isValidating={isOpeningValidation}
              onAccessDecisionClick={handleAccessDecision}
              onContactClick={() => setShowGarantMessageModal(true)}
            />
          )}
        </div>

        {/* ===== RIGHT: Info sidebar (1/4) ===== */}
        <div className="tw-order-1 md:tw-order-2 md:tw-col-span-1 lg:tw-col-span-1 tw-space-y-3">

          {/* Messagerie garant */}
          {!isV1Sav && (
            <div className="tw-rounded-xl tw-border-2 tw-border-affinicar-primary tw-overflow-hidden">
              <SavChatBlock savId={sav.id} channel="garant" title="Messagerie garant" icon={Shield} readOnly={!!isAdmin} unreadHighlightCount={garantUnreadSnapshot ?? 0} />
            </div>
          )}

          {/* Contrat */}
          {sav.contract && (
            <InfoCard title="Contrat" icon={<FileText className="tw-w-4 tw-h-4" />} compact>
              <div className="tw-space-y-0.5">
                <InfoRow
                  label="Numéro"
                  icon={<FileText className="tw-w-3.5 tw-h-3.5" />}
                  value={
                    warranty?.idCompany ? (
                      <button
                        onClick={() => {
                          if (isAdmin) {
                            navigate(`/vendors/${warranty.idCompany}/contracts?contractId=${sav.contract!.id}`);
                          } else {
                            navigate(`/contracts/${sav.contract!.id}`);
                          }
                        }}
                        className="tw-font-medium tw-text-affinicar-primary hover:tw-underline"
                      >
                        {sav.contract.label || `#${sav.contract.id}`}
                      </button>
                    ) : (
                      sav.contract.label || `#${sav.contract.id}`
                    )
                  }
                />
                {profileConfig?.isIntermediary ? (
                  <InfoRow
                    label="Profil"
                    icon={<Briefcase className="tw-w-3.5 tw-h-3.5" />}
                    value={(sav.contract as any).isIntermediary ? 'Intermédiaire de vente' : 'Garant'}
                  />
                ) : null}
                <InfoRow
                  label="Période"
                  value={`${formatDate(sav.contract.dateSouscription)} — ${formatDate(sav.contract.dateEnd)}`}
                  icon={<Calendar className="tw-w-3.5 tw-h-3.5" />}
                />
                {sav.contract.dateSouscription && (
                  <>
                    <InfoRow label="Écoulé" value={formatElapsedDuration(sav.contract.dateSouscription)} icon={<Clock className="tw-w-3.5 tw-h-3.5" />} />
                    <ProgressTimeline
                      startDate={sav.contract.dateSouscription}
                      endDate={sav.contract.dateEnd}
                      className="tw-mt-2"
                    />
                  </>
                )}
              </div>
            </InfoCard>
          )}

          {/* Bénéficiaire */}
          <InfoCard title="Bénéficiaire" icon={<User className="tw-w-4 tw-h-4" />} compact>
            <div className="tw-space-y-0.5">
              <InfoRow label="Nom" value={beneficiary ? `${beneficiary.firstName} ${beneficiary.lastName}` : '—'} icon={<User className="tw-w-3.5 tw-h-3.5" />} />
              <InfoRow
                label="Email"
                value={beneficiary?.email || '—'}
                icon={<Mail className="tw-w-3.5 tw-h-3.5" />}
                href={beneficiary?.email ? `mailto:${beneficiary.email}` : undefined}
              />
              <InfoRow
                label="Téléphone"
                value={beneficiary?.phoneNumber || '—'}
                icon={<Phone className="tw-w-3.5 tw-h-3.5" />}
                href={beneficiary?.phoneNumber ? `tel:${beneficiary.phoneNumber}` : undefined}
              />
            </div>
          </InfoCard>

          {/* Véhicule */}
          <InfoCard title="Véhicule" icon={<Car className="tw-w-4 tw-h-4" />} compact>
            <div className="tw-space-y-0.5">
              <InfoRow label="Marque/Modèle" value={`${vehicleBrand} ${vehicleModel}`} icon={<Car className="tw-w-3.5 tw-h-3.5" />} />
              <InfoRow label="Immatriculation" value={vehicleRegistration || '—'} icon={<CreditCard className="tw-w-3.5 tw-h-3.5" />} />
              <InfoRow label="Date d'achat" value={formatDate(getVehiclePurchaseDate(sav.contract))} icon={<Calendar className="tw-w-3.5 tw-h-3.5" />} />
              <InfoRow label="Mise en circulation" value={formatDate(vehicleFirstRegistrationDate)} icon={<Calendar className="tw-w-3.5 tw-h-3.5" />} />
              <InfoRow label="Kilométrage" value={formatMileage(getVehicleMileage(sav.contract))} icon={<Gauge className="tw-w-3.5 tw-h-3.5" />} hide={getVehicleMileage(sav.contract) == null} />
              <InfoRow
                label="Roulant"
                value={
                  sav.canDrive ? (
                    <span className="tw-text-green-700">Oui, peut rouler</span>
                  ) : (
                    <span className="tw-text-red-700 tw-font-semibold">Non, immobilisé</span>
                  )
                }
                icon={
                  sav.canDrive
                    ? <CheckCircle className="tw-w-3.5 tw-h-3.5 tw-text-green-600" />
                    : <AlertTriangle className="tw-w-3.5 tw-h-3.5 tw-text-red-600" />
                }
                hide={sav.canDrive == null}
              />
            </div>
            {(() => {
              const kmStart = getVehicleMileage(sav.contract);
              const kmPanne = sav.mileage;
              const kmLimit = sav.contract?.warrantyKmLimit || warranty?.kmLimit;
              if (kmStart == null || kmPanne == null || !kmLimit) return null;
              const kmParcourus = kmPanne - kmStart;
              if (kmParcourus < 0) return null;
              const percent = Math.min(Math.round((kmParcourus / kmLimit) * 100), 100);
              const barColor = percent > 90 ? 'tw-bg-red-400' : percent > 75 ? 'tw-bg-yellow-400' : 'tw-bg-green-400';
              return (
                <div className="tw-mt-2 tw-pt-2 tw-border-t tw-border-gray-100">
                  <div className="tw-flex tw-justify-between tw-text-xs tw-text-gray-500 tw-mb-1">
                    <span>{formatMileage(kmParcourus)} parcourus</span>
                    <span>{formatMileage(kmLimit)} max</span>
                  </div>
                  <div className="tw-h-2 tw-bg-gray-200 tw-rounded-full tw-overflow-hidden">
                    <div className={`tw-h-full tw-rounded-full ${barColor}`} style={{ width: `${percent}%` }} />
                  </div>
                  <div className="tw-text-xs tw-text-gray-400 tw-mt-0.5">{percent}% utilisé</div>
                </div>
              );
            })()}
          </InfoCard>

          {/* Garantie */}
          {warranty && (
            <InfoCard title="Garantie" icon={<Shield className="tw-w-4 tw-h-4" />} compact>
              <div className="tw-space-y-0.5">
                <InfoRow
                  label="Nom"
                  icon={<Shield className="tw-w-3.5 tw-h-3.5" />}
                  value={
                    warranty.uuid && warranty.idCompany ? (
                      <button
                        onClick={() => {
                          if (isAdmin) {
                            navigate(`/vendors/${warranty.idCompany}/warranties?warrantyId=${warranty.uuid}`);
                          } else {
                            navigate(`/warranties/${warranty.uuid}`);
                          }
                        }}
                        className="tw-font-medium tw-text-affinicar-primary hover:tw-underline tw-text-left tw-px-2 tw-py-0.5 tw-bg-green-50 tw-rounded-full tw-text-xs tw-border tw-border-green-200"
                      >
                        {warranty.name || '—'}
                      </button>
                    ) : (
                      warranty.name || '—'
                    )
                  }
                />
                <InfoRow
                  label="Durée"
                  value={warranty.duration
                    ? (DURATION_LABELS[warranty.duration as keyof typeof DURATION_LABELS] || `${warranty.duration} mois`)
                    : '—'}
                  icon={<Clock className="tw-w-3.5 tw-h-3.5" />}
                />
                <InfoRow label="Limite KM" value={formatMileage(sav.contract?.warrantyKmLimit || warranty.kmLimit)} icon={<Gauge className="tw-w-3.5 tw-h-3.5" />} />
                <InfoRow label="Plafond" value={formatPrice(totalCap)} icon={<Euro className="tw-w-3.5 tw-h-3.5" />} />
                {financialCapData && totalCap > 0 && (
                  <div className="tw-mt-2 tw-pt-2 tw-border-t tw-border-gray-100">
                    <div className="tw-flex tw-justify-between tw-text-xs tw-mb-1">
                      <span className="tw-text-gray-500">{formatPrice(usedAmount)} utilisé</span>
                      <span className={`${remainingColor} tw-font-semibold`}>{formatPrice(remainingCap)} restant</span>
                    </div>
                    <div className="tw-h-2 tw-bg-gray-200 tw-rounded-full tw-overflow-hidden">
                      <div className={`tw-h-full tw-rounded-full ${capBarColor}`} style={{ width: `${capUsedPercent}%` }} />
                    </div>
                  </div>
                )}
              </div>
            </InfoCard>
          )}

          {/* Autres SAV sur le même contrat */}
          {otherSavs.length > 0 && (
            <InfoCard title={`Autres SAV (${otherSavs.length})`} icon={<Wrench className="tw-w-4 tw-h-4" />} compact>
              <div className="tw-space-y-1">
                {otherSavs.map((s) => {
                  const label = s.uniqueId ? `SAV-${s.uniqueId}` : `#${s.id}`;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => navigate(`/sav/${s.id}`)}
                      className="tw-w-full tw-flex tw-items-center tw-justify-between tw-p-2 tw-rounded-lg hover:tw-bg-gray-50 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-100 tw-text-left"
                    >
                      <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                        <Wrench className="tw-w-3.5 tw-h-3.5 tw-text-gray-400 tw-flex-shrink-0" />
                        <div className="tw-min-w-0">
                          <div className="tw-text-xs tw-font-medium tw-text-gray-900 tw-truncate">{label}</div>
                          <div className="tw-text-xs tw-text-gray-500">{formatDate(s.createdAt)}</div>
                        </div>
                      </div>
                      <span className="tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-bg-gray-100 tw-text-gray-700 tw-flex-shrink-0">
                        {s.isV2 ? 'V2' : 'V1'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </InfoCard>
          )}

          {/* Documents */}
          <InfoCard title="Documents" icon={<Paperclip className="tw-w-4 tw-h-4" />} compact>
            <div className="tw-space-y-3">
              <SavDocumentGrid savId={sav.id} title="Documents partagés" icon={Users} documents={allSharedDocuments} emptyMessage="Aucun document partagé" />
              <div className="tw-border-t tw-border-gray-200" />
              <SavDocumentGrid savId={sav.id} title="Documents internes" icon={Shield} documents={sav.documentsInternal || []} emptyMessage="Aucun document interne" />
            </div>
          </InfoCard>

          {/* Messagerie bénéficiaire */}
          <SavChatBlock savId={sav.id} channel="beneficiary" title="Messagerie bénéficiaire" icon={MessageSquare} />

          {/* Historique */}
          {!isV1Sav && <SavTimelineBlock savId={sav.id} />}

          {/* Technical block (admin only) */}
          {isAdmin && <SavTechnicalBlock sav={sav} />}
        </div>
      </div>
      <SavGarantMessageModal
        isOpen={showGarantMessageModal}
        onClose={() => setShowGarantMessageModal(false)}
        savId={savId}
      />
    </div>
  );
}

// === Timeline / Historique ===
// Config, résumé et détail des événements : `../utils/savTimelineEvents.ts`.

// ==================== Message Modal ====================
function SavMessageModal({ isOpen, onClose, savId, messageId, messageChannel, createdAt, createdByName }: {
  isOpen: boolean; onClose: () => void; savId: number;
  messageId?: number; messageChannel?: 'internal' | 'beneficiary' | 'garant';
  createdAt: string; createdByName?: string;
}) {
  const [message, setMessage] = useState<Message | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && messageId && messageChannel) {
      setLoading(true);
      setError(null);
      SavV2MessagesApi.getMessageByIdAndChannel(savId, messageChannel, messageId)
        .then((msg) => setMessage(msg))
        .catch(() => setError('Impossible de charger le message'))
        .finally(() => setLoading(false));
    }
  }, [isOpen, savId, messageId, messageChannel]);

  if (!isOpen) return null;

  const displayContent = message?.content?.html || message?.content?.text || '';
  const isHtml = !!message?.content?.html;
  const subject = message?.content?.subject;

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4">
      <div className="tw-absolute tw-inset-0 tw-bg-black tw-bg-opacity-50" onClick={onClose} />
      <div className="tw-relative tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-3xl tw-w-full tw-max-h-[80vh] tw-flex tw-flex-col">
        <div className="tw-flex tw-items-center tw-justify-between tw-p-5 tw-border-b">
          <div>
            <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-m-0">{subject || 'Message'}</h3>
            <p className="tw-text-sm tw-text-gray-500 tw-mt-1 tw-mb-0">
              {createdByName && <span>Par {createdByName} — </span>}
              {format(parseISO(createdAt), 'dd/MM/yyyy HH:mm', { locale: fr })}
            </p>
          </div>
          <button onClick={onClose} className="tw-p-2 tw-text-gray-400 hover:tw-text-gray-600 tw-rounded-lg hover:tw-bg-gray-100 tw-transition-colors">
            <XCircle className="tw-w-5 tw-h-5" />
          </button>
        </div>
        <div className="tw-flex-1 tw-overflow-y-auto tw-p-5">
          {loading ? (
            <div className="tw-flex tw-items-center tw-justify-center tw-py-8">
              <Loader2 className="tw-w-6 tw-h-6 tw-animate-spin tw-text-affinicar-primary" />
              <span className="tw-ml-2 tw-text-gray-600">Chargement...</span>
            </div>
          ) : error ? (
            <div className="tw-text-center tw-py-8 tw-text-red-600">
              <AlertCircle className="tw-w-8 tw-h-8 tw-mx-auto tw-mb-2" />
              <p>{error}</p>
            </div>
          ) : isHtml ? (
            <div className="tw-prose tw-prose-sm tw-max-w-none" dangerouslySetInnerHTML={{ __html: displayContent }} />
          ) : (
            <div className="tw-whitespace-pre-wrap tw-text-gray-700">{displayContent}</div>
          )}
        </div>
        <div className="tw-p-4 tw-border-t tw-flex tw-justify-end">
          <button onClick={onClose} className="tw-px-4 tw-py-2 tw-bg-affinicar-primary tw-text-white tw-rounded-lg hover:tw-bg-affinicar-dark tw-transition-colors">Fermer</button>
        </div>
      </div>
    </div>
  );
}

// ==================== Timeline Event Item ====================
function TimelineEventItem({ event, savId }: { event: SavV2Event; savId: number }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showDocViewer, setShowDocViewer] = useState(false);
  const [currentDoc, setCurrentDoc] = useState<IDocument | null>(null);

  const config = getTimelineEventConfig(event.eventType);
  const Icon = config.icon;
  const desc = getTimelineDescription(event);
  const detailRows = isExpanded ? getEventDetailRows(event) : [];
  const m = event.metadata;

  const isMessageEvent = [
    SavV2EventType.MESSAGE_INTERNAL, SavV2EventType.MESSAGE_TO_BENEFICIARY,
    SavV2EventType.MESSAGE_FROM_BENEFICIARY, SavV2EventType.MESSAGE_TO_GARANT, SavV2EventType.MESSAGE_FROM_GARANT,
  ].includes(event.eventType as any);

  const isDocumentEvent = [
    SavV2EventType.QUOTE_UPLOADED, SavV2EventType.DOCUMENT_ADDED,
    SavV2EventType.DOCUMENT_ADDED_INTERNAL, SavV2EventType.QUOTE_SELECTED_FOR_ANALYSIS, SavV2EventType.QUOTE_INVALIDATED,
  ].includes(event.eventType as any);

  // Metadata badges
  const badges: { label: string; value: string; color: 'blue' | 'green' | 'red' | 'yellow' | 'gray' | 'purple' }[] = [];
  if (m?.amount != null) badges.push({ label: 'Montant', value: typeof m.amount === 'number' ? `${(m.amount / 100).toFixed(2)} €` : `${m.amount}`, color: 'green' });
  if (m?.decisionType) {
    const dt: Record<string, string> = { ACCORD: 'Accord', PARTIAL: 'Partiel', REFUSAL: 'Refus' };
    badges.push({ label: 'Type', value: dt[m.decisionType] || m.decisionType, color: m.decisionType === 'REFUSAL' ? 'red' : 'blue' });
  }
  if (m?.repairerName && [SavV2EventType.REPAIRER_ASSIGNED, SavV2EventType.REPAIRER_CONTACTED].includes(event.eventType as any))
    badges.push({ label: 'Réparateur', value: m.repairerName, color: 'purple' });
  if (m?.reason) {
    const reasons: Record<string, string> = { ALREADY_REPAIRED: 'Déjà réparé', REPAIRER_UNAVAILABLE: 'Non disponible', CHANGE_REPAIRER: 'Changement', NO_RESPONSE: 'Pas de réponse', BENEFICIARY_CHOICE: 'Choix bénéficiaire', OTHER: 'Autre' };
    badges.push({ label: 'Raison', value: reasons[m.reason] || m.reason, color: 'yellow' });
  }
  if (m?.hasAttachments) badges.push({ label: 'PJ', value: 'Oui', color: 'blue' });
  if (m?.decision) {
    const dec: Record<string, string> = { approved: 'Approuvé', partial: 'Partiel', rejected: 'Rejeté' };
    badges.push({ label: 'Décision', value: dec[m.decision] || m.decision, color: m.decision === 'rejected' ? 'red' : 'green' });
  }

  const openDoc = (documentId: number, fileName: string) => {
    setCurrentDoc({ id: documentId, label: fileName, fileName, originalName: fileName, path: '', downloadUrl: '', dateCreation: event.createdAt });
    setShowDocViewer(true);
  };

  const downloadDoc = async (documentId: number) => {
    // Nom fourni par le serveur (libellé + extension réelle) : `fileName` des
    // événements est souvent un uuid.
    try { await savV2DocumentApi.downloadDocument(savId, documentId); } catch (e) { console.error('Download error:', e); }
  };

  const badgeColors: Record<string, string> = {
    blue: 'tw-bg-blue-100 tw-text-blue-800 tw-border-blue-200',
    green: 'tw-bg-green-100 tw-text-green-800 tw-border-green-200',
    red: 'tw-bg-red-100 tw-text-red-800 tw-border-red-200',
    yellow: 'tw-bg-yellow-100 tw-text-yellow-800 tw-border-yellow-200',
    gray: 'tw-bg-gray-100 tw-text-gray-700 tw-border-gray-200',
    purple: 'tw-bg-purple-100 tw-text-purple-800 tw-border-purple-200',
  };

  return (
    <>
      <div className="tw-relative tw-flex tw-items-start tw-gap-3">
        {/* Icon */}
        <div className={`tw-relative tw-z-10 tw-flex-shrink-0 tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center ${config.color}`}>
          <Icon className="tw-w-3.5 tw-h-3.5" />
        </div>

        {/* Card */}
        <div className="tw-flex-1 tw-min-w-0 tw-pb-2">
          <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-3 tw-shadow-sm">
            {/* Header row */}
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="tw-w-full tw-text-left tw-bg-transparent tw-border-none tw-p-0 tw-cursor-pointer"
            >
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                <div className="tw-flex tw-items-baseline tw-gap-2 tw-flex-wrap tw-min-w-0">
                  <span className="tw-text-sm tw-font-medium tw-text-gray-900">{config.label}</span>
                  <ChevronDown className={`tw-w-3 tw-h-3 tw-text-gray-400 tw-transition-transform tw-flex-shrink-0 ${isExpanded ? 'tw-rotate-180' : ''}`} />
                </div>
                <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0">
                  {event.createdByName && <span className="tw-text-[10px] tw-text-gray-400 tw-hidden sm:tw-inline">{event.createdByName}</span>}
                  <span className="tw-text-[10px] tw-text-gray-400">{format(parseISO(event.createdAt), 'HH:mm', { locale: fr })}</span>
                </div>
              </div>
            </button>

            {/* Description */}
            {desc && <p className="tw-text-xs tw-text-gray-500 tw-mt-1 tw-mb-0">{desc}</p>}

            {/* Badges */}
            {badges.length > 0 && (
              <div className="tw-flex tw-flex-wrap tw-gap-1 tw-mt-2">
                {badges.map((b, i) => (
                  <span key={i} className={`tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-border ${badgeColors[b.color]}`}>
                    {b.label}: {b.value}
                  </span>
                ))}
              </div>
            )}

            {/* Message action */}
            {isMessageEvent && m?.messageId && m?.messageChannel && (
              <div className="tw-mt-2">
                <button
                  onClick={() => setShowMessageModal(true)}
                  className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1.5 tw-bg-blue-50 tw-text-blue-700 tw-border tw-border-blue-200 tw-rounded-md tw-text-xs hover:tw-bg-blue-100 tw-transition-colors"
                >
                  <Mail className="tw-w-3.5 tw-h-3.5" />
                  Voir le message
                </button>
              </div>
            )}

            {/* Document actions */}
            {isDocumentEvent && m?.documentId && m?.fileName && (
              <div className="tw-flex tw-gap-2 tw-mt-2">
                <button
                  onClick={() => openDoc(m.documentId!, m.fileName!)}
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1.5 tw-bg-blue-50 tw-text-blue-700 tw-border tw-border-blue-200 tw-rounded-md tw-text-xs hover:tw-bg-blue-100 tw-transition-colors"
                >
                  <Eye className="tw-w-3.5 tw-h-3.5" />
                  Voir
                </button>
                <button
                  onClick={() => downloadDoc(m.documentId!)}
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1.5 tw-bg-gray-50 tw-text-gray-700 tw-border tw-border-gray-200 tw-rounded-md tw-text-xs hover:tw-bg-gray-100 tw-transition-colors"
                >
                  <Download className="tw-w-3.5 tw-h-3.5" />
                  Télécharger
                </button>
              </div>
            )}

            {/* Notes */}
            {m?.notes && (
              <div className="tw-mt-2 tw-p-2.5 tw-bg-gray-50 tw-rounded tw-text-xs tw-text-gray-700 tw-border-l-4 tw-border-blue-200">
                <span className="tw-font-medium tw-text-gray-800">Notes : </span>
                {m.notes}
              </div>
            )}

            {/* Expandable details */}
            {isExpanded && detailRows.length > 0 && (
              <div className="tw-mt-2 tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-space-y-1.5">
                {detailRows.map((row, i) => (
                  <div key={i} className="tw-flex tw-gap-2 tw-text-xs">
                    <span className="tw-text-gray-400 tw-font-medium tw-flex-shrink-0 tw-min-w-[80px]">{row.label}</span>
                    <span className="tw-text-gray-700 tw-break-words tw-min-w-0">{row.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Message Modal */}
      <SavMessageModal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        savId={savId}
        messageId={m?.messageId}
        messageChannel={m?.messageChannel as 'internal' | 'beneficiary' | 'garant' | undefined}
        createdAt={event.createdAt}
        createdByName={event.createdByName}
      />

      {/* Document Viewer */}
      {showDocViewer && currentDoc && (
        <DocumentViewer
          document={currentDoc}
          isOpen={showDocViewer}
          onClose={() => setShowDocViewer(false)}
          onDownload={async (doc: IDocument) => { await downloadDoc(doc.id); }}
          title={`Document — ${currentDoc.fileName}`}
          getTokenUrl={(docId: number) => `/sav-v2/${savId}/documents/${docId}/get-download-token`}
        />
      )}
    </>
  );
}

/** Taille d'une page d'historique (l'API plafonne à 100). */
const TIMELINE_PAGE_SIZE = 50;

type SavEventsPage = { nodes: SavV2Event[]; pageInfos: { totalCount: number; hasNextPage: boolean } };

function SavTimelineBlock({ savId }: { savId: number }) {
  // L'API filtre selon l'audience (le vendeur voit tout sauf le chat interne
  // et ses pièces jointes) AVANT la pagination : le front affiche tout.
  const { data, isLoading, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['sav-events', savId],
    queryFn: async ({ pageParam = 0 }) => {
      const { data } = await axiosInstance.get<SavEventsPage>(`/sav-v2/${savId}/events`, {
        params: { page: pageParam, limit: TIMELINE_PAGE_SIZE },
      });
      return data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((count, page) => count + page.nodes.length, 0);
      return lastPage.nodes.length > 0 && loaded < lastPage.pageInfos.totalCount ? allPages.length : undefined;
    },
    staleTime: 30_000,
  });

  const totalCount = data?.pages[0]?.pageInfos?.totalCount || 0;

  // Pages à plat ; dédoublonnées (un event créé entre deux pages décale l'offset).
  const filteredEvents = useMemo(() => {
    const byId = new Map<number, SavV2Event>();
    data?.pages.forEach((page) => page.nodes.forEach((event) => byId.set(event.id, event)));
    return [...byId.values()];
  }, [data]);

  // Regroupement par jour
  const groupedEvents = useMemo(() => {
    const groups: Record<string, SavV2Event[]> = {};
    filteredEvents.forEach((event) => {
      const date = parseISO(event.createdAt);
      let dayLabel: string;
      if (isToday(date)) dayLabel = "Aujourd'hui";
      else if (isYesterday(date)) dayLabel = 'Hier';
      else dayLabel = format(date, 'dd MMMM yyyy', { locale: fr });
      if (!groups[dayLabel]) groups[dayLabel] = [];
      groups[dayLabel].push(event);
    });
    // Trier chaque groupe par date descendante (plus récent en premier), fallback par id
    Object.values(groups).forEach((group) => {
      group.sort((a, b) => {
        const diff = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        return diff !== 0 ? diff : b.id - a.id;
      });
    });
    return groups;
  }, [filteredEvents]);

  const sortedDays = Object.keys(groupedEvents);


  if (isLoading) {
    return (
      <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-p-4">
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
          <Clock className="tw-w-4 tw-h-4 tw-text-gray-400" />
          <span className="tw-text-sm tw-font-semibold tw-text-gray-700">Historique</span>
        </div>
        <div className="tw-animate-pulse tw-space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="tw-h-8 tw-bg-gray-100 tw-rounded" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-b tw-border-gray-100">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Clock className="tw-w-4 tw-h-4 tw-text-gray-400" />
          <span className="tw-text-sm tw-font-semibold tw-text-gray-700">Historique</span>
          <span className="tw-text-xs tw-text-gray-400">({totalCount})</span>
        </div>
        <button
          onClick={() => refetch()}
          className="tw-p-1.5 tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-rounded-lg tw-transition-colors"
          title="Actualiser"
        >
          <RefreshCw className="tw-w-4 tw-h-4" />
        </button>
      </div>

      {/* Timeline content */}
      <div className="tw-p-4 tw-max-h-[600px] tw-overflow-y-auto">
        {filteredEvents.length === 0 ? (
          <div className="tw-text-center tw-py-8">
            <Clock className="tw-w-8 tw-h-8 tw-text-gray-300 tw-mx-auto tw-mb-2" />
            <p className="tw-text-sm tw-text-gray-400 tw-mb-0">Aucun événement</p>
          </div>
        ) : (
          <div className="tw-space-y-6">
            {sortedDays.map((day) => (
              <div key={day}>
                {/* Day separator */}
                <div className="tw-flex tw-items-center tw-gap-3 tw-mb-3">
                  <span className="tw-px-2.5 tw-py-0.5 tw-bg-gray-900 tw-text-white tw-rounded-full tw-text-xs tw-font-medium">
                    {day}
                  </span>
                  <div className="tw-flex-1 tw-h-px tw-bg-gray-200" />
                  <span className="tw-text-[10px] tw-text-gray-400">
                    {groupedEvents[day].length} évt{groupedEvents[day].length > 1 ? 's' : ''}
                  </span>
                </div>

                {/* Events */}
                <div className="tw-relative tw-ml-2">
                  <div className="tw-absolute tw-left-3.5 tw-top-0 tw-bottom-0 tw-w-px tw-bg-gray-200" />
                  <div className="tw-space-y-2">
                    {groupedEvents[day].map((event) => (
                      <TimelineEventItem key={event.id} event={event} savId={savId} />
                    ))}
                  </div>
                </div>
              </div>
            ))}
            {hasNextPage && (
              <div className="tw-text-center">
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-bg-gray-50 tw-text-gray-700 tw-border tw-border-gray-200 tw-rounded-md tw-text-xs hover:tw-bg-gray-100 tw-transition-colors disabled:tw-opacity-50"
                >
                  {isFetchingNextPage && <Loader2 className="tw-w-3.5 tw-h-3.5 tw-animate-spin" />}
                  Voir plus ({totalCount - filteredEvents.length} restant{totalCount - filteredEvents.length > 1 ? 's' : ''})
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
